import { api } from "@/lib/api";

// Loads Snap.js exactly once, using the client key + environment from the backend config.
let snapPromise = null;

async function ensureSnap() {
  if (snapPromise) return snapPromise;
  snapPromise = (async () => {
    const { data } = await api.get("/payments/midtrans/config");
    if (!data.configured) throw new Error("Midtrans belum dikonfigurasi.");
    const src = data.is_production
      ? "https://app.midtrans.com/snap/snap.js"
      : "https://app.sandbox.midtrans.com/snap/snap.js";
    if (!window.snap) {
      await new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = src;
        s.setAttribute("data-client-key", data.client_key);
        s.onload = resolve;
        s.onerror = () => { snapPromise = null; reject(new Error("Gagal memuat Snap.js")); };
        document.body.appendChild(s);
      });
    }
    return true;
  })();
  return snapPromise;
}

// While Snap is open, prevent clicks from leaking to the app/modal behind the popup.
// Root cause of the leak: Radix Dialog sets body { pointer-events: none } while keeping
// DialogContent { pointer-events: auto } (often inline !important). The Snap iframe (appended
// to <body>) then inherits `none` and clicks fall through to the DialogContent behind it.
// Fix: (1) force the Snap subtree back to pointer-events:auto at the top z-index, and
// (2) drop a transparent full-viewport SHIELD just below Snap that swallows any stray click —
// beating inline !important without fighting the cascade. Both are removed when Snap closes.
function lockBackgroundForSnap() {
  const shield = document.createElement("div");
  shield.id = "snap-bg-shield";
  shield.style.cssText = "position:fixed;inset:0;background:transparent;";
  shield.style.setProperty("z-index", "2147483646", "important");
  shield.style.setProperty("pointer-events", "auto", "important");
  document.body.appendChild(shield);

  const style = document.createElement("style");
  style.id = "snap-bg-lock";
  // Snap is appended AFTER this and defaults to z-index 2147483647 (above the shield).
  style.textContent =
    'iframe[src*="midtrans"], [id^="snap-midtrans"], .snap-container, .snap-overlay {' +
    " z-index: 2147483647 !important; pointer-events: auto !important; }";
  document.head.appendChild(style);

  return () => { shield.remove(); style.remove(); };
}

// Revalidates slots + creates a Snap token on the server, then opens the popup.
// Resolves with { status: "success" | "pending" | "error" | "closed" }.
export async function payWithMidtrans({ kind, id, amount }) {
  await ensureSnap();
  const { data } = await api.post("/payments/midtrans/snap-token", { kind, id, amount });
  const release = lockBackgroundForSnap();
  return new Promise((resolve) => {
    let released = false;
    const done = (payload) => {
      if (!released) { released = true; release(); }
      resolve(payload);
    };
    window.snap.pay(data.snap_token, {
      onSuccess: (result) => done({ status: "success", result }),
      onPending: (result) => done({ status: "pending", result }),
      onError: (result) => done({ status: "error", result }),
      onClose: () => done({ status: "closed" }),
    });
  });
}

// ---- Public (patient) order payment via secure token link ----
let snapPromisePublic = null;
async function ensureSnapPublic() {
  if (snapPromisePublic) return snapPromisePublic;
  snapPromisePublic = (async () => {
    const { data } = await api.get("/public/payments/midtrans/config");
    if (!data.configured) throw new Error("Midtrans belum dikonfigurasi.");
    const src = data.is_production
      ? "https://app.midtrans.com/snap/snap.js"
      : "https://app.sandbox.midtrans.com/snap/snap.js";
    if (!window.snap) {
      await new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = src;
        s.setAttribute("data-client-key", data.client_key);
        s.onload = resolve;
        s.onerror = () => { snapPromisePublic = null; reject(new Error("Gagal memuat Snap.js")); };
        document.body.appendChild(s);
      });
    }
    return true;
  })();
  return snapPromisePublic;
}

export async function payWithMidtransPublic({ token, amount }) {
  await ensureSnapPublic();
  const { data } = await api.post(`/public/payments/midtrans/order/${token}/snap-token`, { amount });
  const release = lockBackgroundForSnap();
  return new Promise((resolve) => {
    let released = false;
    const done = (payload) => { if (!released) { released = true; release(); } resolve(payload); };
    window.snap.pay(data.snap_token, {
      onSuccess: (result) => done({ status: "success", result }),
      onPending: (result) => done({ status: "pending", result }),
      onError: (result) => done({ status: "error", result }),
      onClose: () => done({ status: "closed" }),
    });
  });
}

export async function payWithMidtransReservationPublic({ rid }) {
  await ensureSnapPublic();
  const { data } = await api.post(`/public/payments/midtrans/reservation/${rid}/snap-token`, {});
  const release = lockBackgroundForSnap();
  return new Promise((resolve) => {
    let released = false;
    const done = (payload) => { if (!released) { released = true; release(); } resolve(payload); };
    window.snap.pay(data.snap_token, {
      onSuccess: (result) => done({ status: "success", result }),
      onPending: (result) => done({ status: "pending", result }),
      onError: (result) => done({ status: "error", result }),
      onClose: () => done({ status: "closed" }),
    });
  });
}

// Token-based (shareable link) single-reservation payment.
export async function payWithMidtransReservationTokenPublic({ token }) {
  await ensureSnapPublic();
  const { data } = await api.post(`/public/pay/${token}/snap-token`, {});
  const release = lockBackgroundForSnap();
  return new Promise((resolve) => {
    let released = false;
    const done = (payload) => { if (!released) { released = true; release(); } resolve(payload); };
    window.snap.pay(data.snap_token, {
      onSuccess: (result) => done({ status: "success", result }),
      onPending: (result) => done({ status: "pending", result }),
      onError: (result) => done({ status: "error", result }),
      onClose: () => done({ status: "closed" }),
    });
  });
}
